(() => {
  'use strict';

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const liveRegion = () => document.querySelector('.lh-live-region');

  const createEventId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `lh-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const META_STANDARD_EVENTS = new Set(['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout']);
  const SHOPIFY_FUNNEL_EVENTS = {
    PageView: 'lhabitude:page_view',
    ViewContent: 'lhabitude:view_content',
    AddToCart: 'lhabitude:add_to_cart',
    InitiateCheckout: 'lhabitude:initiate_checkout'
  };
  const pendingMetaEvents = [];
  const initializedMetaPixels = new Set();
  let privacyListenerBound = false;

  const readCookie = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const match = document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : '';
  };

  const getVisitorId = () => {
    const storageKey = 'lhabitude_meta_visitor_id';
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) return stored;
      const visitorId = createEventId();
      window.localStorage.setItem(storageKey, visitorId);
      return visitorId;
    } catch (error) {
      return createEventId();
    }
  };

  const getFbc = () => {
    const stored = readCookie('_fbc');
    if (stored) return stored;
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    return fbclid ? `fb.1.${Date.now()}.${fbclid}` : '';
  };

  const publishToShopifyPixels = (eventName, payload, attempt = 0) => {
    const mapped = SHOPIFY_FUNNEL_EVENTS[eventName];
    if (!mapped) return;

    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish === 'function') {
      try {
        Promise.resolve(publish.call(window.Shopify.analytics, mapped, payload)).catch(() => {});
      } catch (error) {
        // The event remains available to the future API and DOM listeners.
      }
      return;
    }

    if (attempt < 8) {
      window.setTimeout(() => publishToShopifyPixels(eventName, payload, attempt + 1), 250);
    }
  };

  const metaProcessingAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return true;
    try {
      const analyticsAllowed = typeof privacy.analyticsProcessingAllowed === 'function'
        ? privacy.analyticsProcessingAllowed()
        : true;
      const marketingAllowed = typeof privacy.marketingAllowed === 'function'
        ? privacy.marketingAllowed()
        : true;
      return Boolean(analyticsAllowed && marketingAllowed);
    } catch (error) {
      return false;
    }
  };

  const ensureMetaPixel = (pixelId) => {
    if (!pixelId) return false;

    if (!window.fbq) {
      const fbq = function () { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
      window.fbq = fbq;
      window._fbq = fbq;
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript?.parentNode?.insertBefore(script, firstScript);
    }

    if (!initializedMetaPixels.has(pixelId)) {
      window.fbq('init', pixelId);
      initializedMetaPixels.add(pixelId);
    }
    return true;
  };

  const sendToMetaBrowser = (root, payload) => {
    const pixelId = root.dataset.metaPixelId;
    if (!ensureMetaPixel(pixelId)) return;

    const parameters = {
      content_ids: payload.variant_id ? [String(payload.variant_id)] : [],
      content_type: 'product',
      content_name: payload.product_title || '',
      currency: payload.currency || 'EUR',
      value: Number(payload.value || 0),
      quantity: Number(payload.quantity || 1),
      funnel_stage: payload.funnel_stage || '',
      funnel_progress: Number(payload.funnel_progress || 0)
    };
    const method = META_STANDARD_EVENTS.has(payload.event_name) ? 'track' : 'trackCustom';
    window.fbq(method, payload.event_name, parameters, { eventID: payload.event_id });
  };

  const sendToCapiEndpoint = (root, payload) => {
    const endpoint = root.dataset.eventsEndpoint;
    if (!endpoint) return;
    const body = JSON.stringify(payload);

    if (navigator.sendBeacon) {
      const queued = navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      if (queued) return;
    }

    fetch(endpoint, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
      keepalive: true,
      credentials: 'omit'
    }).catch(() => {});
  };

  const flushPendingMetaEvents = () => {
    if (!metaProcessingAllowed()) return;
    while (pendingMetaEvents.length) {
      const { root, payload } = pendingMetaEvents.shift();
      sendToMetaBrowser(root, payload);
      sendToCapiEndpoint(root, payload);
    }
  };

  const sendToMetaWhenAllowed = (root, payload) => {
    if (metaProcessingAllowed()) {
      sendToMetaBrowser(root, payload);
      sendToCapiEndpoint(root, payload);
      return;
    }

    pendingMetaEvents.push({ root, payload });
    if (!privacyListenerBound) {
      privacyListenerBound = true;
      document.addEventListener('visitorConsentCollected', flushPendingMetaEvents);
    }
  };

  const trackFunnelEvent = (root, eventName, detail = {}) => {
    if (!root || !eventName) return null;

    const cents = Number(root.dataset.productValue || 0);
    const payload = {
      event_name: eventName,
      event_id: createEventId(),
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: window.location.href,
      action_source: 'website',
      page_path: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer || '',
      product_id: root.dataset.productId || '',
      variant_id: root.dataset.variantId || '',
      product_handle: root.dataset.productHandle || '',
      product_title: root.dataset.productTitle || '',
      value: Number.isFinite(cents) ? cents / 100 : 0,
      currency: root.dataset.currency || '',
      client_user_agent: navigator.userAgent,
      fbp: readCookie('_fbp'),
      fbc: getFbc(),
      external_id: getVisitorId(),
      ...detail
    };

    publishToShopifyPixels(eventName, payload);
    window.dispatchEvent(new CustomEvent('lhabitude:funnel-event', { detail: payload }));
    sendToMetaWhenAllowed(root, payload);

    return payload;
  };

  class HeaderOverlay {
    constructor(header, targets) {
      this.header = header;
      this.active = new Set();

      if (!targets.length || !('IntersectionObserver' in window)) {
        header.classList.add('is-scrolled');
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.active.add(entry.target);
          else this.active.delete(entry.target);
        });
        header.classList.toggle('lh-header--over-hero', this.active.size > 0);
      }, { threshold: 0, rootMargin: '-72px 0px 0px 0px' });

      targets.forEach((target) => this.observer.observe(target));
    }
  }

  class AutoplayVideo {
    constructor(video) {
      // iOS/Android browsers sometimes ignore the autoplay HTML attribute
      // (slow network, video not yet decodable, or the section is still
      // off-screen) and never retry on their own. Force the same muted/inline
      // state via JS and nudge play() ourselves as soon as the video's own
      // section actually enters the viewport; if the browser still refuses
      // (autoplay policy), fall back to starting on the visitor's first tap.
      this.video = video;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      this.attemptPlay = this.attemptPlay.bind(this);

      if ('IntersectionObserver' in window) {
        // Observer la SECTION du video, pas le video lui-meme : dans les
        // carrousels horizontaux (texture/rituel), overflow-x CLIPPE les
        // cartes 2 et 3 — l'observer du video ne se declenchait qu'au swipe
        // (ecran noir ~2s pendant le telechargement). La section, elle, entre
        // dans le viewport ~600px avant : tous ses clips demarrent chargement
        // + lecture ensemble, avant que la visiteuse n'arrive.
        const target = video.closest('section') || video;
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.attemptPlay();
          });
        }, { threshold: 0, rootMargin: '600px' });
        this.observer.observe(target);
      } else {
        this.attemptPlay();
      }
    }

    attemptPlay() {
      if (this.video.preload === 'none') this.video.preload = 'auto';
      const playback = this.video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => this.armFallback());
      }
    }

    armFallback() {
      const resume = () => {
        this.video.play().catch(() => {});
        document.removeEventListener('touchstart', resume);
        document.removeEventListener('click', resume);
      };
      document.addEventListener('touchstart', resume, { once: true, passive: true });
      document.addEventListener('click', resume, { once: true });
    }
  }

  class FrameSequence {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('[data-scroll-canvas]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.chapters = [...root.querySelectorAll('[data-chapter-index]')];
      this.progressEl = root.querySelector('[data-scroll-progress]');
      this.target = 0;
      this.current = 0;
      this.frameIndex = -1;
      this.images = [];
      this.frameSources = [];
      this.preloaded = false;
      this.completed = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const base = root.dataset.frameBase;
      const suffix = root.dataset.frameSuffix || '';
      const count = parseInt(root.dataset.frameCount, 10) || 0;
      const digits = parseInt(root.dataset.frameDigits, 10) || 3;

      if (this.canvas && base && count > 0) {
        for (let i = 1; i <= count; i += 1) {
          this.frameSources.push(`${base}${String(i).padStart(digits, '0')}${suffix}`);
        }
        this.loadFirstFrame();
        this.armPreload();
      }

      if (root.hasAttribute('data-view-content')) {
        trackFunnelEvent(root, 'ViewContent', { funnel_stage: 'scroll_view', funnel_progress: 0 });
      }

      new QuickAdd(root);

      this.resize = this.resize.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.tick = this.tick.bind(this);
      window.addEventListener('resize', this.resize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });

      // Scroll-lock: wheel and touch scrolling are never intercepted — a real
      // scroll gesture always passes through every intermediate pixel (fast or
      // slow), so it can never actually skip the sequence, only wheel/finger
      // speed changes how quickly it plays through. The only genuine "skip"
      // vectors are the keys that jump straight to a scroll position without
      // passing through the frames in between (End/Home/PageDown/Space); those
      // stay blocked while this section is pinned and hasn't finished once.
      if (!this.reducedMotion) {
        this.onKeydown = this.onKeydown.bind(this);
        window.addEventListener('keydown', this.onKeydown);
      }

      this.resize();
      this.onScroll();
      requestAnimationFrame(this.tick);
    }

    // The first frame doubles as the static poster for the whole sequence, so
    // it is always fetched immediately.
    loadFirstFrame() {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => this.draw(0, true);
      img.src = this.frameSources[0];
      this.images[0] = img;
    }

    // The remaining frames are only fetched once the section is roughly one
    // and a half viewports away. Downloading all 121 stills at DOMContentLoaded
    // competed with the offer at the top of the page on mobile connections,
    // which is exactly the traffic the page is optimised for.
    armPreload() {
      if (!('IntersectionObserver' in window)) {
        this.preload();
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          this.preload();
        }
      }, { rootMargin: '150% 0px' });

      observer.observe(this.root);
    }

    preload() {
      if (this.preloaded) return;
      this.preloaded = true;

      for (let i = 1; i < this.frameSources.length; i += 1) {
        const img = new Image();
        img.decoding = 'async';
        img.src = this.frameSources[i];
        this.images[i] = img;
      }
    }

    resize() {
      if (!this.canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(this.canvas.clientWidth * dpr);
      this.canvas.height = Math.round(this.canvas.clientHeight * dpr);
      this.draw(this.frameIndex, true);
    }

    onScroll() {
      const rect = this.root.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      this.target = total > 0 ? clamp(-rect.top / total) : (rect.top <= 0 ? 1 : 0);
      if (this.target >= 0.999) this.completed = true;
    }

    isPinned() {
      const rect = this.root.getBoundingClientRect();
      return rect.top <= 0 && rect.bottom > window.innerHeight;
    }

    isLocked() {
      return !this.completed && this.isPinned();
    }

    // End/Home jump straight to the top/bottom of the document and PageDown/
    // Space jump a full viewport, all without passing through the scroll
    // positions in between — the only inputs that could actually skip the
    // sequence. Block just those while locked; everything else (wheel,
    // trackpad, touch, arrow keys) scrolls natively at whatever speed the
    // visitor uses.
    onKeydown(event) {
      const jumpKeys = ['End', 'Home', 'PageDown', ' '];
      if (!jumpKeys.includes(event.key) || !this.isLocked()) return;
      event.preventDefault();
    }

    draw(index, force) {
      if (!this.ctx || index < 0 || !this.images.length) return;
      const img = this.images[index];
      if (!img || !img.complete || !img.naturalWidth) return;
      if (index === this.frameIndex && !force) return;
      this.frameIndex = index;

      const cw = this.canvas.width;
      const ch = this.canvas.height;
      if (!cw || !ch) return;
      const ir = img.naturalWidth / img.naturalHeight;
      const cr = cw / ch;
      let dw, dh, dx, dy;
      if (ir > cr) { dh = ch; dw = ch * ir; dx = (cw - dw) / 2; dy = 0; }
      else { dw = cw; dh = cw / ir; dx = 0; dy = (ch - dh) / 2; }

      this.ctx.clearRect(0, 0, cw, ch);
      this.ctx.drawImage(img, dx, dy, dw, dh);
    }

    tick() {
      // Track the scroll position directly (no lerp/easing lag). Any smoothing
      // here means the frame keeps drifting toward the target after the
      // visitor has already stopped scrolling, which reads as sluggish/"duro"
      // instead of a video that's directly tied to the scroll gesture.
      this.current = this.target;

      if (this.images.length) {
        const index = Math.min(this.images.length - 1, Math.max(0, Math.round(this.current * (this.images.length - 1))));
        this.draw(index);
      }

      const count = this.chapters.length || 1;
      const travel = 56;
      this.chapters.forEach((chapter, i) => {
        const start = i / count;
        const end = (i + 1) / count;
        const local = clamp((this.current - start) / (end - start));
        const fadeIn = clamp(local / 0.22);
        const fadeOut = clamp((1 - local) / 0.22);
        const opacity = Math.min(fadeIn, fadeOut);
        const translateY = (local - 0.5) * travel;
        chapter.style.opacity = String(opacity);
        chapter.style.transform = `translateY(${translateY.toFixed(1)}px)`;
      });

      if (this.progressEl) this.progressEl.style.transform = `scaleX(${this.current})`;

      requestAnimationFrame(this.tick);
    }
  }

  class QuickAdd {
    constructor(root) {
      this.trackingRoot = root;
      this.form = root.querySelector('.lh-scroll__cta-form');
      this.button = root.querySelector('[data-quick-add]');
      if (!this.form || !this.button) return;
      this.submit = this.submit.bind(this);
      this.button.addEventListener('click', this.submit);
    }

    async submit(event) {
      event.preventDefault();
      if (this.button.disabled) return;
      this.button.disabled = true;
      this.button.classList.add('is-loading');

      try {
        const formData = new FormData(this.form);
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!response.ok) throw new Error('Cart request failed');

        trackFunnelEvent(this.trackingRoot, 'AddToCart', {
          funnel_stage: 'quick_add_button',
          funnel_progress: 100,
          quantity: Number(formData.get('quantity') || 1),
          variant_id: String(formData.get('id') || this.trackingRoot.dataset.variantId || '')
        });

        window.location.assign(`${root}cart`);
      } catch (error) {
        this.form.submit();
      }
    }
  }

  class SectionTracker {
    constructor(root) {
      if (!('IntersectionObserver' in window)) {
        this.fire(root);
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.fire(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.4 });
      this.observer.observe(root);
    }

    fire(root) {
      const stage = root.dataset.trackSection;
      if (!stage) return;
      window.dispatchEvent(new CustomEvent('lhabitude:section-view', {
        detail: { section: stage, page_path: window.location.pathname }
      }));
    }
  }

  class ScrollReveal {
    constructor(root) {
      if (!('IntersectionObserver' in window)) {
        root.classList.add('is-visible');
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
      this.observer.observe(root);
    }
  }

  class ViewContentTracker {
    constructor(root) {
      this.root = root;
      this.trackingRoot = root.closest('[data-product-card]') || root;

      if (!('IntersectionObserver' in window)) {
        this.fire();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.fire();
        }
      }, { threshold: 0.55 });

      this.observer.observe(root);
    }

    fire() {
      if (this.root.dataset.viewContentSent === 'true') return;
      this.root.dataset.viewContentSent = 'true';
      trackFunnelEvent(this.trackingRoot, 'ViewContent', {
        funnel_stage: 'offer_view',
        funnel_progress: 10
      });
    }
  }

  class ProductCard {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.lh-purchase__form, .lh-cro__form');
      this.variantSelect = root.querySelector('[data-variant-select]');
      this.variantInput = root.querySelector('[data-variant-id]');
      this.price = root.querySelector('[data-product-price]');
      this.buyButtons = [...root.querySelectorAll('[data-buy-now]')];
      this.addButtons = [...root.querySelectorAll('[data-add-to-cart]')];
      this.productData = this.readProductData();
      if (!this.form) return;
      this.bind();
    }

    readProductData() {
      const script = this.root.querySelector('[data-product-json]');
      if (!script) return null;
      try { return JSON.parse(script.textContent); } catch (error) { return null; }
    }

    bind() {
      if (this.variantSelect) this.variantSelect.addEventListener('change', () => this.onVariantChange());
      this.buyButtons.forEach((button) => button.addEventListener('click', (event) => this.submit(event, 'checkout')));
      this.addButtons.forEach((button) => button.addEventListener('click', (event) => this.submit(event, 'stay')));
    }

    onVariantChange() {
      const option = this.variantSelect.selectedOptions[0];
      if (!option || !this.variantInput) return;
      this.variantInput.value = option.value;
      const available = option.dataset.available === 'true';
      const price = option.dataset.price;
      if (this.price && price) this.price.textContent = price;
      this.root.dataset.variantId = option.value;
      if (option.dataset.value) this.root.dataset.productValue = option.dataset.value;
      [...this.buyButtons, ...this.addButtons].forEach((button) => { button.disabled = !available; });
    }

    async submit(event, destination) {
      event.preventDefault();
      const button = event.currentTarget;
      if (!button || button.disabled) return;
      const goToCheckout = destination === 'checkout';
      const originalText = button.textContent;
      const actionButtons = [...this.buyButtons, ...this.addButtons];
      actionButtons.forEach((action) => { action.disabled = true; });
      button.classList.add('is-loading');

      try {
        const formData = new FormData(this.form);
        if (goToCheckout) {
          trackFunnelEvent(this.root, 'InitiateCheckout', {
            funnel_stage: 'buy_button',
            funnel_progress: 100,
            quantity: Number(formData.get('quantity') || 1),
            variant_id: String(formData.get('id') || this.root.dataset.variantId || '')
          });
        }
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!response.ok) throw new Error('Cart request failed');

        if (goToCheckout) {
          window.location.assign(`${root}checkout`);
          return;
        }

        trackFunnelEvent(this.root, 'AddToCart', {
          funnel_stage: 'add_to_cart_button',
          funnel_progress: 100,
          quantity: Number(formData.get('quantity') || 1),
          variant_id: String(formData.get('id') || this.root.dataset.variantId || '')
        });

        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        document.querySelectorAll('[data-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
          count.hidden = cart.item_count === 0;
        });
        const region = liveRegion();
        if (region) region.textContent = this.root.dataset.addedMessage || originalText;
        button.textContent = this.root.dataset.addedMessage || originalText;
        setTimeout(() => { button.textContent = originalText; }, 1600);
      } catch (error) {
        const returnTo = this.form.querySelector('[data-checkout-return]');
        if (returnTo && goToCheckout) returnTo.disabled = false;
        this.form.submit();
        return;
      } finally {
        actionButtons.forEach((action) => { action.disabled = false; });
        button.classList.remove('is-loading');
      }
    }
  }

  const init = (scope = document) => {
    if (scope === document) {
      const header = document.querySelector('[data-lh-header]');
      const targets = [...document.querySelectorAll('[data-transparent-header]')];
      if (header && !header.dataset.overlayBound) {
        header.dataset.overlayBound = 'true';
        new HeaderOverlay(header, targets);
      }

      if (!document.body.dataset.pageviewSent) {
        document.body.dataset.pageviewSent = 'true';
        trackFunnelEvent(document.body, 'PageView', { funnel_stage: 'page_view', funnel_progress: 0 });
      }
    }

    // .lh-lazy-video: sections sous la ligne de flottaison rendues SANS attribut
    // autoplay (sinon le navigateur precharge la video entiere au chargement).
    // AutoplayVideo les demarre a l'intersection, comme les video[autoplay].
    scope.querySelectorAll('video[autoplay]:not([data-initialized]), video.lh-lazy-video:not([data-initialized])').forEach((video) => {
      video.dataset.initialized = 'true';
      new AutoplayVideo(video);
    });

    scope.querySelectorAll('[data-scroll-video]:not([data-initialized])').forEach((root) => {
      root.dataset.initialized = 'true';
      new FrameSequence(root);
    });

    scope.querySelectorAll('[data-scroll-reveal]:not([data-initialized])').forEach((root) => {
      root.dataset.initialized = 'true';
      new ScrollReveal(root);
    });

    scope.querySelectorAll('[data-view-content]:not([data-scroll-video]):not([data-view-content-initialized])').forEach((root) => {
      root.dataset.viewContentInitialized = 'true';
      new ViewContentTracker(root);
    });

    scope.querySelectorAll('[data-product-card]:not([data-initialized])').forEach((root) => {
      root.dataset.initialized = 'true';
      new ProductCard(root);
    });

    scope.querySelectorAll('[data-track-section]:not([data-track-initialized])').forEach((root) => {
      root.dataset.trackInitialized = 'true';
      new SectionTracker(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
